import { useEffect, useRef, useState } from 'react';
import { GripVertical, Workflow, Flag } from 'lucide-react';
import {
  draggable,
  dropTargetForElements,
} from '@atlaskit/pragmatic-drag-and-drop/element/adapter';
import { useTranslation } from 'react-i18next';
import { UserAvatar } from '@/shared/components/custom/avatars/UserAvatar';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/shared/components/ui/tooltip';
import { cn } from '@/core/lib/utils';
import { PALETTE } from '@/core/lib/colors';
import type { Task, TaskPriority } from '../../types';
import { DateDisplay } from '../DateDisplay';
import { OverflowMenu } from '../OverflowMenu';

function deterministicColor(userId: string): string {
  const hash = userId.split('').reduce((acc, c) => acc + c.charCodeAt(0), 0);
  return PALETTE[hash % PALETTE.length].token;
}

const PRIORITY_COLOR: Record<TaskPriority, string> = {
  urgent: 'text-chart-5',
  high: 'text-chart-3',
  medium: 'text-chart-4',
  low: 'text-chart-2',
};

interface TaskCardProps {
  task: Task;
  onClick?: () => void;
  onEdit?: () => void;
  onDelete?: () => void;
}

export function TaskCard({ task, onClick, onEdit, onDelete }: TaskCardProps) {
  const { t } = useTranslation('tasks');
  const cardRef = useRef<HTMLDivElement>(null);
  const handleRef = useRef<HTMLButtonElement>(null);
  const [isDragging, setIsDragging] = useState(false);

  useEffect(() => {
    const el = cardRef.current;
    const handle = handleRef.current;
    if (!el || !handle) return;

    const cleanupDraggable = draggable({
      element: el,
      dragHandle: handle,
      getInitialData: () => ({ taskId: task.id, statusId: task.status.id }),
      onDragStart: () => setIsDragging(true),
      onDrop: () => setIsDragging(false),
    });

    const cleanupDropTarget = dropTargetForElements({
      element: el,
      getData: () => ({ taskId: task.id, statusId: task.status.id }),
    });

    return () => {
      cleanupDraggable();
      cleanupDropTarget();
    };
  }, [task.id, task.status.id]);

  const subtaskCount = task.subtasks?.length ?? task.subtaskCount ?? 0;

  return (
    <div
      ref={cardRef}
      data-testid={`tasks-board-card-${task.id}`}
      className={cn(
        'group/card relative rounded-md border border-border bg-card p-3 shadow-sm transition-opacity',
        isDragging && 'opacity-40',
        onClick && 'cursor-pointer',
      )}
    >
      <div className="flex items-start gap-2">
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              type="button"
              ref={handleRef}
              data-testid={`tasks-board-card-${task.id}-drag-handle`}
              className="mt-0.5 shrink-0 cursor-grab text-muted-foreground opacity-0 transition-opacity group-hover/card:opacity-100 active:cursor-grabbing"
              aria-label={t('boardView.card.dragTooltip')}
              onClick={(e) => e.stopPropagation()}
            >
              <GripVertical className="size-4" />
            </button>
          </TooltipTrigger>
          <TooltipContent>{t('boardView.card.dragTooltip')}</TooltipContent>
        </Tooltip>

        <div className="min-w-0 flex-1" onClick={onClick}>
          <p className="truncate text-sm font-medium leading-snug">{task.title}</p>

          <div className="mt-2 flex items-center gap-2">
            <span className={cn(!task.assignee && 'invisible')}>
              {task.assignee && (
                <UserAvatar
                  name={task.assignee.name}
                  color={deterministicColor(task.assignee.id)}
                  size="xs"
                />
              )}
            </span>

            <div className="ml-auto flex items-center gap-2">
              <span
                data-testid={`tasks-board-card-${task.id}-subtask-count`}
                className={cn(
                  'flex items-center gap-0.5 text-xs text-muted-foreground',
                  subtaskCount === 0 && 'invisible',
                )}
              >
                <Workflow className="size-3 shrink-0" />
                {subtaskCount}
              </span>
              <span className={cn(!task.dueDate && 'invisible')}>
                <DateDisplay
                  date={task.dueDate}
                  variant="due"
                  statusType={task.status.type}
                  className="text-xs"
                  showIcon
                />
              </span>
              <Flag
                className={cn(
                  'size-3 shrink-0',
                  !task.priority ? 'invisible' : PRIORITY_COLOR[task.priority],
                )}
                aria-label={task.priority ? t(`priority.${task.priority}`) : undefined}
              />
            </div>
          </div>
        </div>

        <div onClick={(e) => e.stopPropagation()}>
          <OverflowMenu
            data-testid={`tasks-board-card-${task.id}-overflow`}
            groupName="card"
            onEdit={onEdit}
            onDelete={onDelete}
          />
        </div>
      </div>
    </div>
  );
}
